import utils from "@/utils";
import storeutils from "@/stores/utils";
import Vue from "vue";
import axios from "@/plugins/axios";
import store from "@/stores/store";
import vuetify from "@/plugins/vuetify";
import BulkResultDialog from "@/components/common/bulk-result-dialog.vue";

/**
 * Map bulk API errors to display labels using selected items.
 * @param {Array} errors - Array of [id, message] tuples from the API
 * @param {Array} [items] - Selected items (must expose `id`)
 * @param {Function} [getIdFn] - (item) => display label
 * @returns {Array} Array of [label, message] tuples
 */
function resolveBulkErrors(errors, items, getIdFn) {
    if (!errors?.length) {
        return [];
    }
    return errors.map(([rawId, message]) => {
        if (!items?.length || !getIdFn) {
            return [rawId, message];
        }
        const item = items.find(
            (entry) => entry?.id === rawId || String(entry?.id) === String(rawId),
        );
        if (!item) {
            return [rawId, message];
        }
        return [getIdFn(item), message];
    });
}

/**
 * Mount a BulkResultDialog programmatically.
 * @param {Array} errors - Array of [label, message] tuples
 * @param {Array} successes - Array of successful ids
 */
function showBulkResultDialog(errors, successes) {
    const rawLocale = store.state.session?.active_locale || "en";
    const locale = rawLocale.split("-")[0].split("_")[0];

    const DialogConstructor = Vue.extend(BulkResultDialog);
    const instance = new DialogConstructor({
        vuetify,
        propsData: { errors, successes, locale },
    });
    const container = document.createElement("div");
    document.body.appendChild(container);
    instance.$mount(container);
}

/**
 * Helper function to execute bulk action API call and handle file downloads
 * This function is called by stores to execute bulk actions
 * @param {Object} params - Parameters object
 * @param {String} params.bulkActionUrl - URL for the bulk action endpoint (e.g., "/api/customerinvoices/bulk/")
 * @param {String} params.action - Action name (e.g., "validate", "delete", "download")
 * @param {Array} params.instances - Array of selected item IDs
 * @param {Object} [params.payload] - Optional payload for the action
 * @param {Object} [params.context] - Optional Vue component context for handling downloads and messages
 * @param {Array} [params.items] - Selected items (for resolving error labels via getIdFn)
 * @param {Function} [params.getIdFn] - Optional function to get display label from an item
 * @param {String} [params.successMessage] - Optional custom success message
 * @returns {Promise} Promise that resolves with the result
 */
export async function executeBulkAction(params) {
    const {
        bulkActionUrl,
        action,
        instances,
        payload,
        context,
        items,
        getIdFn,
        successMessage,
        suppressSuccessMessage,
    } = params;

    try {
        // Make the API call
        const response = await axios.post(bulkActionUrl, {
            action: action,
            instances: instances,
            payload: payload || {},
        });

        if (params.recoverResult) response.data = await params.recoverResult(response.data);

        const result = {
            ...response,
            items: instances,
        };

        // Handle file downloads if the response contains files and context is provided
        if (context && result?.data?.files && Array.isArray(result.data.files) && result.data.files.length > 0) {
            for (const fileUrl of result.data.files) {
                const filename = utils.extractFilenameFromUrl(fileUrl) || "download.zip";
                await utils.downloadFile(fileUrl, filename);
            }
        }

        // Handle success/error messages if context is provided
        if (context && result?.data) {
            const { success, errors } = result.data;

            if (errors && errors.length > 0) {
                const displayErrors = resolveBulkErrors(errors, items, getIdFn);
                showBulkResultDialog(displayErrors, success || []);
            } else if (success && success.length > 0) {
                // All succeeded
                if (!suppressSuccessMessage) {
                    if (successMessage) {
                        context.addAlert(successMessage);
                    } else {
                        context.addAlert(context.$translate("bulkCompleted") || "Bulk action completed successfully");
                    }
                }
            }
        }

        return result;
    } catch (error) {
        if (context) {
            context.$swal({
                title: context.$translate("error"),
                html: context.$utils.resultToSwalContent(
                    context,
                    storeutils.handleError(error),
                ),
                type: "error",
            });
        }
        throw error;
    }
}
